import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  FaTimes, FaCog, FaClipboardCheck, FaStar, FaSearch, FaComment, FaComments, FaUser,
  FaFolder, FaFileUpload, FaCheck, FaChartLine,
} from 'react-icons/fa';
import DocumentsManager from './DocumentsManager';
import AnnualReportTab from './AnnualReportTab';
import RegisteredAgentTab from './RegisteredAgentTab';
import BookkeepingMenuTab from './BookkeepingMenuTab';
import TaxFilingTab from './TaxFilingTab';
import UsaFormationTab from './UsaFormationTab';

const TABS = [
  { id: 'services', label: 'Services', icon: FaCog },
  { id: 'primary', label: 'Tax Filing', icon: FaClipboardCheck },
  { id: 'usa-formation', label: 'USA Formation', icon: FaStar },
  { id: 'documents', label: 'Documents', icon: FaFolder },
  { id: 'annual-report', label: 'Annual Report', icon: FaFileUpload },
  { id: 'registered-agent', label: 'Registered Agent', icon: FaCheck },
  { id: 'bookkeeping', label: 'Bookkeeping', icon: FaChartLine },
];

const cardClass = 'p-4 rounded-xl bg-gray-50 dark:bg-neutral-800/50 border border-gray-100 dark:border-neutral-700';

const ClientMenuModal = ({
  showMenu,
  setShowMenu,
  initialTab = 'services',
  clients = [],
  onlineUserIds = [],
  selectedMenuUser,
  setSelectedMenuUser,
  handleFormAssignment,
  handleMemberFormAssignment,
  handleStepSubmit,
  handleMemberStepSubmit,
  isStepSubmitted,
  isMemberStepSubmitted,
  canSubmitStep,
  canSubmitMemberStep,
  toggleServiceLock,
  handleMemberNameUpdate,
  addMember,
  removeMember,
  onChatClick,
  handleAnnualReportServiceUpdate,
  handleRemoveAnnualReportService,
  handleBookkeepingServiceUpdate,
  handleRemoveBookkeepingService,
  handleRegisteredAgentDueDateUpdate,
  handleRegisteredAgentDocumentUpload,
  handleRegisteredAgentDocumentDelete,
  handleAnnualReportStepStatusChange,
  handleReopenAnnualReportChecklist,
  userSteps,
  refreshUserSteps,
  handleTaxDateUpdate,
  handleMemberTaxDateUpdate,
  handlePaymentStatusUpdate,
  handleMoveToHistory,
  handleUnlockTaxFiling,
  handleUpdateTaxSummaryTable,
  handleHistoryTaxDateUpdate,
  handleOpenStep1Checklist,
  onPrepareChecklist,
  getStep3Status,
  updateClientFromUser,
  initialMemberIndex,
  handleAnnualReportPaymentStatusUpdate,
}) => {
  const [taxFilingSubTab, setTaxFilingSubTab] = useState('primary-member');
  const [activeMemberIndex, setActiveMemberIndex] = useState(0);
  const [activeTab, setActiveTab] = useState('services');
  const [switcherSearch, setSwitcherSearch] = useState('');
  const [sidebarWidth, setSidebarWidth] = useState(960);
  const [isResizing, setIsResizing] = useState(false);

  useEffect(() => {
    if (showMenu) {
      setActiveTab(initialTab);
      if (initialMemberIndex !== undefined && initialMemberIndex !== null && initialMemberIndex >= 0) {
        setActiveMemberIndex(initialMemberIndex);
        setTaxFilingSubTab('member');
      } else if (initialMemberIndex === -1) {
        setTaxFilingSubTab('primary-member');
      }
    }
  }, [showMenu, initialTab, initialMemberIndex]);

  useEffect(() => {
    if (!showMenu) return undefined;
    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = original;
    };
  }, [showMenu]);

  useEffect(() => {
    if (!isResizing) return undefined;

    const handleMouseMove = (e) => {
      const newWidth = window.innerWidth - e.clientX;
      if (newWidth > 600 && newWidth < window.innerWidth * 0.95) {
        setSidebarWidth(newWidth);
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);

  const handleTabSelect = (tab) => {
    setActiveTab(tab);
    if (tab !== 'primary') {
      setTaxFilingSubTab('primary-member');
    }
  };

  const handleClientSwitch = (user) => {
    setSelectedMenuUser(user);
    setTaxFilingSubTab('primary-member');
    setActiveMemberIndex(0);
  };

  const handleMemberSwitch = (user, memberIndex) => {
    setSelectedMenuUser(user);
    setActiveMemberIndex(memberIndex);
    setTaxFilingSubTab('member');
    setActiveTab('primary');
  };

  const filteredClients = clients.filter(
    (user) =>
      user.name.toLowerCase().includes(switcherSearch.toLowerCase())
      || user.email.toLowerCase().includes(switcherSearch.toLowerCase())
      || (user.members && user.members.some((m) => m.name?.toLowerCase().includes(switcherSearch.toLowerCase())))
  );

  if (!showMenu || !selectedMenuUser) return null;

  const renderServicesTab = () => (
    <div>
      <h5 className="font-bold text-gray-900 dark:text-white mb-4">Service Lock/Unlock</h5>
      <div className={`${cardClass} space-y-1`}>
        {[
          { id: 'usa-company-switch', label: 'USA Company Formation', key: 'isUSAEnabled' },
          { id: 'tax-filing-switch', label: 'Tax Filing', key: 'isTaxFilingEnabled' },
          { id: 'bookkeeping-switch', label: 'Bookkeeping', key: 'isBookkeepingEnabled' },
          { id: 'cfo-switch', label: 'CFO Advisory', key: 'isCFOEnabled' },
          { id: 'registered-agent-switch', label: 'Registered Agent', key: 'isRegisteredAgentEnabled' },
          { id: 'annual-report-switch', label: 'Annual Report Service', key: 'isAnnualReportServiceEnabled' },
        ].map((service) => (
          <div key={service.id} className="flex items-center justify-between py-3 border-b border-gray-100 dark:border-neutral-700 last:border-0">
            <span className="text-sm font-medium text-gray-800 dark:text-gray-200">{service.label}</span>
            <button
              type="button"
              role="switch"
              aria-checked={selectedMenuUser?.[service.key] || false}
              onClick={() => toggleServiceLock(selectedMenuUser._id, service.label, !selectedMenuUser?.[service.key])}
              className={`relative inline-flex h-6 w-11 shrink-0 rounded-full transition-colors ${
                selectedMenuUser?.[service.key] ? 'bg-primary-600' : 'bg-gray-300 dark:bg-neutral-600'
              }`}
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform mt-0.5 ${
                  selectedMenuUser?.[service.key] ? 'translate-x-5' : 'translate-x-0.5'
                }`}
              />
            </button>
          </div>
        ))}
      </div>
    </div>
  );

  const renderPrimaryTab = () => (
    <TaxFilingTab
      selectedMenuUser={selectedMenuUser}
      setSelectedMenuUser={setSelectedMenuUser}
      userSteps={userSteps}
      refreshUserSteps={refreshUserSteps}
      handleFormAssignment={handleFormAssignment}
      handleMemberFormAssignment={handleMemberFormAssignment}
      handleMemberNameUpdate={handleMemberNameUpdate}
      addMember={addMember}
      removeMember={removeMember}
      handleTaxDateUpdate={handleTaxDateUpdate}
      handleMemberTaxDateUpdate={handleMemberTaxDateUpdate}
      handlePaymentStatusUpdate={handlePaymentStatusUpdate}
      handleMoveToHistory={handleMoveToHistory}
      handleUnlockTaxFiling={handleUnlockTaxFiling}
      handleUpdateTaxSummaryTable={handleUpdateTaxSummaryTable}
      handleHistoryTaxDateUpdate={handleHistoryTaxDateUpdate}
      handleOpenStep1Checklist={handleOpenStep1Checklist}
      onPrepareChecklist={onPrepareChecklist}
      getStep3Status={getStep3Status}
      updateClientFromUser={updateClientFromUser}
      initialMemberIndex={initialMemberIndex}
    />
  );

  const renderUsaTab = () => (
    <UsaFormationTab
      selectedMenuUser={selectedMenuUser}
      setSelectedMenuUser={setSelectedMenuUser}
      updateClientFromUser={updateClientFromUser}
    />
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'services': return renderServicesTab();
      case 'primary': return renderPrimaryTab();
      case 'usa-formation': return renderUsaTab();
      case 'documents':
        return (
          <DocumentsManager
            userId={selectedMenuUser?._id}
            userName={selectedMenuUser?.name}
            hideBackButton
          />
        );
      case 'annual-report':
        return (
          <AnnualReportTab
            selectedMenuUser={selectedMenuUser}
            handleAnnualReportServiceUpdate={handleAnnualReportServiceUpdate}
            handleRemoveAnnualReportService={handleRemoveAnnualReportService}
            handleAnnualReportStepStatusChange={handleAnnualReportStepStatusChange}
            handleReopenAnnualReportChecklist={handleReopenAnnualReportChecklist}
            handleAnnualReportPaymentStatusUpdate={handleAnnualReportPaymentStatusUpdate}
          />
        );
      case 'registered-agent':
        return (
          <RegisteredAgentTab
            selectedMenuUser={selectedMenuUser}
            handleRegisteredAgentDueDateUpdate={handleRegisteredAgentDueDateUpdate}
            handleRegisteredAgentDocumentUpload={handleRegisteredAgentDocumentUpload}
            handleRegisteredAgentDocumentDelete={handleRegisteredAgentDocumentDelete}
          />
        );
      case 'bookkeeping':
        return (
          <BookkeepingMenuTab
            selectedMenuUser={selectedMenuUser}
            setSelectedMenuUser={setSelectedMenuUser}
            handleBookkeepingServiceUpdate={handleBookkeepingServiceUpdate}
            handleRemoveBookkeepingService={handleRemoveBookkeepingService}
          />
        );
      default: return null;
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[1200] flex" onClick={() => setShowMenu(false)}>
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />

      <div
        className="ml-auto relative flex h-full shadow-2xl bg-white dark:bg-neutral-900 animate-slide-in-right"
        onClick={(e) => e.stopPropagation()}
        style={{ width: `${sidebarWidth}px`, maxWidth: '95vw' }}
      >
        {/* Resize handle */}
        <div
          onMouseDown={(e) => {
            e.preventDefault();
            setIsResizing(true);
          }}
          className={`absolute left-0 top-0 w-1.5 h-full cursor-col-resize z-50 group ${
            isResizing ? 'bg-primary-500' : 'hover:bg-primary-400/30'
          }`}
        >
          <div className="absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 w-0.5 h-12 bg-gray-300 dark:bg-neutral-600 rounded-full group-hover:bg-primary-400" />
        </div>

        {/* Client switcher */}
        <div className="w-64 xl:w-72 bg-white dark:bg-neutral-900 border-r border-gray-200 dark:border-neutral-800 flex flex-col h-full shrink-0">
          <div className="p-4 border-b border-gray-100 dark:border-neutral-800 bg-gray-50/50 dark:bg-neutral-800/50">
            <h6 className="text-[10px] uppercase font-bold text-gray-500 dark:text-gray-400 tracking-widest mb-3">
              Client Switcher
            </h6>
            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-3 h-3" />
              <input
                type="text"
                placeholder="Search clients..."
                value={switcherSearch}
                onChange={(e) => setSwitcherSearch(e.target.value)}
                className="w-full pl-9 pr-3 py-2 bg-white dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700 rounded-xl text-xs text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 outline-none"
              />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto p-2 space-y-1 scrollbar-hide">
            {filteredClients.map((user) => (
              <div key={user._id} className="space-y-0.5">
                <button
                  type="button"
                  onClick={() => handleClientSwitch(user)}
                  className={`w-full flex items-center gap-3 p-3 rounded-xl transition-all text-left ${
                    selectedMenuUser?._id === user._id && taxFilingSubTab === 'primary-member'
                      ? 'bg-primary-50 dark:bg-primary-900/20 border border-primary-100 dark:border-primary-800'
                      : 'hover:bg-gray-50 dark:hover:bg-neutral-800 border border-transparent'
                  }`}
                >
                  <div className="relative shrink-0">
                    {user.profilePicture ? (
                      <img
                        src={user.profilePicture}
                        alt={user.name}
                        className="w-8 h-8 rounded-full object-cover border border-primary-200/50"
                        onError={(e) => { e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(user.name)}&background=random&color=fff`; }}
                      />
                    ) : (
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                        selectedMenuUser?._id === user._id
                          ? 'bg-primary-600 text-white'
                          : 'bg-gray-200 dark:bg-neutral-700 text-gray-600 dark:text-gray-400'
                      }`}>
                        {user.name.charAt(0).toUpperCase()}
                      </div>
                    )}
                    {onlineUserIds.includes(user._id) && (
                      <span
                        className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-emerald-500 border-2 border-white dark:border-neutral-900 rounded-full animate-pulse"
                        title="Online Now"
                      />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className={`text-xs font-bold truncate flex items-center gap-1.5 ${
                      selectedMenuUser?._id === user._id ? 'text-primary-700 dark:text-primary-400' : 'text-gray-900 dark:text-white'
                    }`}>
                      <span className="truncate">{user.name}</span>
                      {onlineUserIds.includes(user._id) && (
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" title="Active Now" />
                      )}
                    </p>
                    <p className="text-[10px] text-gray-500 truncate">{user.email}</p>
                  </div>
                </button>
                {user.members && user.members.length > 0 && (
                  <div className="ml-11 space-y-0.5 border-l-2 border-gray-100 dark:border-neutral-700 pl-3 pb-1">
                    {user.members.map((member, mIdx) => (
                      <button
                        key={mIdx}
                        type="button"
                        onClick={() => handleMemberSwitch(user, mIdx)}
                        className={`w-full flex items-center gap-2 py-1.5 px-2 rounded-lg text-left transition-all ${
                          selectedMenuUser?._id === user._id && activeMemberIndex === mIdx && taxFilingSubTab === 'member'
                            ? 'bg-primary-50/50 dark:bg-primary-900/10 text-primary-700 dark:text-primary-400 font-bold'
                            : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                        }`}
                      >
                        <FaUser className="w-2 h-2 shrink-0" />
                        <span className="text-[11px] truncate">{member.name || `Member ${mIdx + 1}`}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {filteredClients.length === 0 && (
              <p className="text-center text-xs text-gray-400 py-8">No clients found</p>
            )}
          </div>
        </div>

        {/* Vertical tabs */}
        <div className="w-16 bg-gradient-to-b from-black via-neutral-900 to-black flex flex-col items-center py-4 gap-1 shrink-0">
          <button
            type="button"
            onClick={() => setShowMenu(false)}
            className="w-10 h-10 flex items-center justify-center text-red-400 hover:text-white bg-red-900/20 hover:bg-red-600 rounded-xl transition-all mb-6 border border-red-900/40 group"
            title="Close"
          >
            <FaTimes className="group-hover:rotate-90 transition-transform duration-300" />
          </button>
          {TABS.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => handleTabSelect(tab.id)}
                className={`w-12 h-12 flex flex-col items-center justify-center rounded-xl transition-all group relative ${
                  activeTab === tab.id
                    ? 'bg-primary-600 text-white shadow-lg shadow-primary-600/30'
                    : 'text-gray-500 hover:text-white hover:bg-white/10'
                }`}
                title={tab.label}
              >
                <Icon className="w-4 h-4" />
                <span className="text-[7px] mt-0.5 font-medium leading-tight text-center truncate w-full px-0.5">
                  {tab.label.split(' ')[0]}
                </span>
              </button>
            );
          })}
        </div>

        {/* Main content */}
        <div className="flex-1 flex flex-col bg-white dark:bg-neutral-900 overflow-hidden min-w-0">
          <div className="px-6 py-4 border-b border-gray-100 dark:border-neutral-800 bg-gradient-to-r from-gray-50 to-white dark:from-neutral-900 dark:to-neutral-900 shrink-0">
            <div className="flex items-center gap-4">
              <div className="relative shrink-0">
                {selectedMenuUser?.profilePicture ? (
                  <img
                    src={selectedMenuUser.profilePicture}
                    alt={selectedMenuUser.name}
                    className="w-11 h-11 rounded-xl object-cover shadow-lg border-2 border-primary-100 dark:border-primary-900/40"
                    onError={(e) => { e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(selectedMenuUser.name)}&background=random&color=fff`; }}
                  />
                ) : (
                  <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center text-white font-bold text-lg shadow-lg">
                    {selectedMenuUser?.name?.charAt(0)?.toUpperCase() || 'C'}
                  </div>
                )}
                {onlineUserIds.includes(selectedMenuUser?._id) && (
                  <span
                    className="absolute -bottom-1 -right-1 w-3.5 h-3.5 bg-emerald-500 border-2 border-white dark:border-neutral-900 rounded-full animate-pulse"
                    title="Online Now"
                  />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h2 className="text-lg font-bold text-gray-900 dark:text-white truncate">
                    {selectedMenuUser?.name || 'Client'}
                  </h2>
                  {onlineUserIds.includes(selectedMenuUser?._id) && (
                    <span className="flex items-center gap-1 text-[10px] font-extrabold uppercase text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/30 px-2 py-0.5 rounded-full border border-emerald-200/50 dark:border-emerald-800/50">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-ping" />
                      Active
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{selectedMenuUser?.email}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {onChatClick && (
                  <button
                    type="button"
                    onClick={() => onChatClick(selectedMenuUser)}
                    className="flex items-center gap-1.5 px-3.5 py-1.5 bg-primary-600 hover:bg-primary-700 text-white text-xs font-bold rounded-xl transition-all shadow-md shadow-primary-500/20 cursor-pointer active:scale-95"
                    title="Open Chat with Client"
                  >
                    <img src="/chat-icon.png" className="w-4 h-4 object-contain" alt="Chat" />
                    <span>Chat</span>
                  </button>
                )}
                <span className="px-3 py-1 text-xs font-semibold rounded-full bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400 border border-primary-200 dark:border-primary-800">
                  {TABS.find((t) => t.id === activeTab)?.label}
                </span>
              </div>
            </div>
          </div>

          <div className="flex-1 p-6 overflow-y-auto scrollbar-hide">
            {renderTabContent()}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ClientMenuModal;
